import styled from 'styled-components';

import {
  darkenColor,
  isLightColor,
  lightenColor,
  polarToCartesian
} from './predictionUtils';

/*
  Web port of the mobile prediction dial (components/AnimatedWheel/StaticWheel).
  Pure SVG: equal-angle wedges with the same convex shading (surface gradient,
  edge shadow, top-left highlight, beveled rim), ambient underlay disc, gloss
  arc and white hub. Clicks land on the wedge paths directly, so no coordinate
  hit-testing is needed.
*/

// Geometry — mirrors WHEEL_CONSTANTS on mobile
const START_ANGLE = -90; // first segment starts at the top
const BORDER_INSET = 8; // default rim band width (px)
const SEGMENT_GAP_DEG = 0.5; // per-side angular gap between tiles
const HUB_SIZE_RATIO = 180 / 350; // hub diameter vs wheel diameter
const HUB_BORDER_COLOR = '#b4b4b4';

// Shading knobs — mirrors SEGMENT_SHADING on mobile
const SHADING = {
  base: { centerLighten: 1, midLighten: 0.18, edgeDarken: 0.18, radiusFactor: 0.85 },
  edgeShadow: {
    darkenAmount: 0.95,
    radiusFactor: 0.95,
    innerOpacity: 0,
    midOpacity: 0.22,
    edgeOpacity: 0.95,
    rimOpacity: 0.9
  },
  highlight: {
    color: '#FFFFFF',
    radiusFactor: 0.6,
    offsetX: -0.14,
    offsetY: -0.16,
    centerOpacity: 0.6,
    midOpacity: 0.1,
    edgeOpacity: 0
  },
  CENTROID_RADIUS_FACTOR: 0.6
};

function describeArc(cx, cy, outerRadius, startAngle, endAngle) {
  const angleDiff = Math.abs(endAngle - startAngle);
  if (angleDiff < 0.01) return '';

  const outerStart = polarToCartesian(cx, cy, outerRadius, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, endAngle);
  const largeArcFlag = angleDiff > 180 ? 1 : 0;

  return `M ${outerStart.x} ${outerStart.y} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y} L ${cx} ${cy} Z`;
}

function describeRingArc(cx, cy, outerRadius, innerRadius, startAngle, endAngle) {
  const angleDiff = Math.abs(endAngle - startAngle);
  if (angleDiff < 0.01) return '';

  const outerStart = polarToCartesian(cx, cy, outerRadius, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, endAngle);
  const largeArcFlag = angleDiff > 180 ? 1 : 0;

  return `M ${outerStart.x} ${outerStart.y} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y} L ${innerEnd.x} ${innerEnd.y} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y} Z`;
}

/*
  Per-segment rim inset, tapered by how close the wedge sits to the top of the
  wheel: the two topmost wedges get no rim, the next pairs get thin rims, and
  everything else gets the full BORDER_INSET band.
*/
function getSegmentBorderInsets(segmentAngles) {
  const map = new Map();
  if (!segmentAngles.length) return map;

  const distances = segmentAngles.map(({ startAngle, endAngle }, i) => {
    const mid = (startAngle + endAngle) / 2;
    let d = Math.abs((mid + 90) % 360);
    if (d > 180) d = 360 - d;
    return { i, d };
  });
  distances.sort((a, b) => a.d - b.d);

  const insetByRank = [0, 0, 3, 3, 6, 6];
  distances.forEach(({ i }, rank) => {
    if (rank < insetByRank.length) map.set(i, insetByRank[rank]);
  });

  return map;
}

const WheelContainer = styled.div`
  position: relative;
  /* 3D coin tilt — same perspective transform as the mobile wheel */
  transform: perspective(1000px) rotateX(15deg) rotateY(-5deg);
`;

const SegmentGroup = styled.g`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const SegmentLabel = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  font-family: CeraPro;
`;

const PercentageBadge = styled.div`
  margin-top: 3px;
  padding: 1px 4.5px;
  border-radius: 4.5px;
  font-weight: 700;
`;

const HubOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const Hub = styled.div`
  position: relative;
  background-color: #fff;
  border: 2px solid ${HUB_BORDER_COLOR};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  /* the overlay lets clicks through to the wedges; the hub itself swallows
     them so tapping the center never selects a segment underneath */
  pointer-events: auto;
`;

const WheelSegment = ({
  segment,
  index,
  cx,
  cy,
  outerRadius,
  startAngle,
  endAngle,
  borderInset,
  isActive,
  activeColor,
  clickable,
  onClick
}) => {
  const start = startAngle + SEGMENT_GAP_DEG;
  const end = endAngle - SEGMENT_GAP_DEG;

  const fillColor = isActive && activeColor ? activeColor : segment.color;
  const hasBorder = borderInset > 0;
  const innerOuterRadius = outerRadius - borderInset;

  const surfacePath = describeArc(cx, cy, innerOuterRadius, start, end);
  const rimPath = describeRingArc(cx, cy, outerRadius, innerOuterRadius, start, end);
  const outlinePath = describeArc(cx, cy, outerRadius, start, end);

  const centerColor = lightenColor(fillColor, SHADING.base.centerLighten);
  const midColor = lightenColor(fillColor, SHADING.base.midLighten);
  const edgeColor = darkenColor(fillColor, SHADING.base.edgeDarken);
  const edgeShadowTint = darkenColor(fillColor, SHADING.edgeShadow.darkenAmount);

  const surfaceGradId = `seg_surface_${index}`;
  const edgeShadowGradId = `seg_edge_shadow_${index}`;
  const highlightGradId = `seg_highlight_${index}`;
  const rimGradId = `seg_rim_${index}`;

  const midAngle = (start + end) / 2;
  const centroid = polarToCartesian(
    cx,
    cy,
    innerOuterRadius * SHADING.CENTROID_RADIUS_FACTOR,
    midAngle
  );

  const highlightAnchor = {
    x: centroid.x + innerOuterRadius * SHADING.highlight.offsetX,
    y: centroid.y + innerOuterRadius * SHADING.highlight.offsetY
  };

  return (
    <SegmentGroup $clickable={clickable} onClick={clickable ? () => onClick(segment) : undefined}>
      <defs>
        {/* Convex base — brighter at the wedge centroid, darkened at the edge */}
        <radialGradient
          id={surfaceGradId}
          cx={centroid.x}
          cy={centroid.y}
          r={innerOuterRadius * SHADING.base.radiusFactor}
          fx={centroid.x}
          fy={centroid.y}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={centerColor} stopOpacity="1" />
          <stop offset="55%" stopColor={midColor} stopOpacity="1" />
          <stop offset="100%" stopColor={edgeColor} stopOpacity="1" />
        </radialGradient>

        {/* Perimeter darkening for depth between tiles */}
        <radialGradient
          id={edgeShadowGradId}
          cx={centroid.x}
          cy={centroid.y}
          r={innerOuterRadius * SHADING.edgeShadow.radiusFactor}
          fx={centroid.x}
          fy={centroid.y}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="50%" stopColor={edgeShadowTint} stopOpacity={SHADING.edgeShadow.innerOpacity} />
          <stop offset="80%" stopColor={edgeShadowTint} stopOpacity={SHADING.edgeShadow.midOpacity} />
          <stop offset="95%" stopColor={edgeShadowTint} stopOpacity={SHADING.edgeShadow.edgeOpacity} />
          <stop offset="100%" stopColor={edgeShadowTint} stopOpacity={SHADING.edgeShadow.rimOpacity} />
        </radialGradient>

        {/* Soft top-left spec highlight */}
        <radialGradient
          id={highlightGradId}
          cx={highlightAnchor.x}
          cy={highlightAnchor.y}
          r={innerOuterRadius * SHADING.highlight.radiusFactor}
          fx={highlightAnchor.x}
          fy={highlightAnchor.y}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={SHADING.highlight.color} stopOpacity={SHADING.highlight.centerOpacity} />
          <stop offset="55%" stopColor={SHADING.highlight.color} stopOpacity={SHADING.highlight.midOpacity} />
          <stop offset="100%" stopColor={SHADING.highlight.color} stopOpacity={SHADING.highlight.edgeOpacity} />
        </radialGradient>

        {/* Beveled rim — light top-left to dark bottom-right */}
        <linearGradient
          id={rimGradId}
          x1={cx - outerRadius * 0.7}
          y1={cy - outerRadius * 0.7}
          x2={cx + outerRadius * 0.7}
          y2={cy + outerRadius * 0.7}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
          <stop offset="35%" stopColor={lightenColor(fillColor, 0.5)} stopOpacity="1" />
          <stop offset="70%" stopColor={darkenColor(fillColor, 0.05)} stopOpacity="0.8" />
          <stop offset="100%" stopColor={darkenColor(fillColor, 0.35)} stopOpacity="1" />
        </linearGradient>
      </defs>

      {hasBorder && <path d={rimPath} fill={isActive ? fillColor : `url(#${rimGradId})`} />}

      {isActive ? (
        <path d={surfacePath} fill={fillColor} fillOpacity={0.8} />
      ) : (
        <>
          <path d={surfacePath} fill={`url(#${surfaceGradId})`} />
          <path d={surfacePath} fill={`url(#${edgeShadowGradId})`} />
          <path d={surfacePath} fill={`url(#${highlightGradId})`} />
        </>
      )}

      <path d={outlinePath} fill="none" stroke="#000" strokeWidth={0.5} strokeLinejoin="round" />
    </SegmentGroup>
  );
};

const PredictionWheel = ({
  segments,
  diameter = 350,
  activeSegmentId = null,
  activeColor,
  clickable = false,
  onSegmentClick,
  centerContent
}) => {
  const outerRadius = diameter / 2;
  const cx = outerRadius;
  const cy = outerRadius;

  const hubSize = Math.round(diameter * HUB_SIZE_RATIO);
  const innerRadius = hubSize / 2;

  const anglePerSegment = 360 / segments.length;
  const segmentAngles = segments.map((_, i) => ({
    startAngle: START_ANGLE + i * anglePerSegment,
    endAngle: START_ANGLE + (i + 1) * anglePerSegment
  }));
  const borderInsets = getSegmentBorderInsets(segmentAngles);

  // Outer-rim gloss highlight arc across the upper edge
  const glossStart = polarToCartesian(cx, cy, outerRadius - 2, -160);
  const glossEnd = polarToCartesian(cx, cy, outerRadius - 2, -20);
  const glossArcPath = `M ${glossStart.x} ${glossStart.y} A ${outerRadius - 2} ${outerRadius - 2} 0 0 1 ${glossEnd.x} ${glossEnd.y}`;

  // Labels sit at the wedge midpoint between hub and rim
  const labelRadius = innerRadius + (outerRadius - innerRadius) * 0.5;

  /*
    Label sizes scale with the wheel like the 350px mobile design, but are
    capped so the longest score always fits inside its wedge: limited by the
    arc width at the label radius and by the radial band between hub and rim
    (which also holds the percentage badge). Prevents cut-off text on wheels
    with many segments or long labels.
  */
  const maxLabelLength = Math.max(1, ...segments.map((s) => (s.label || '').length));
  const arcWidth = 2 * Math.PI * labelRadius * (anglePerSegment / 360);
  const labelFontSize = Math.max(
    12,
    Math.round(
      Math.min(
        diameter * 0.1,
        (arcWidth * 0.85) / (maxLabelLength * 0.62),
        (outerRadius - innerRadius) * 0.5
      )
    )
  );
  const badgeFontSize = Math.max(10, Math.round(diameter * (13 / 350)));

  return (
    <WheelContainer style={{ width: diameter, height: diameter }}>
      <svg width={diameter} height={diameter} viewBox={`0 0 ${diameter} ${diameter}`}>
        {/* Underlay disc the tile gaps show through */}
        <circle cx={cx} cy={cy} r={outerRadius} fill="#c8c8c8" />

        {segments.map((segment, index) => (
          <WheelSegment
            key={segment.id}
            segment={segment}
            index={index}
            cx={cx}
            cy={cy}
            outerRadius={outerRadius}
            startAngle={segmentAngles[index].startAngle}
            endAngle={segmentAngles[index].endAngle}
            borderInset={borderInsets.has(index) ? borderInsets.get(index) : BORDER_INSET}
            isActive={segment.id === activeSegmentId}
            activeColor={activeColor}
            clickable={clickable}
            onClick={onSegmentClick}
          />
        ))}

        <path
          d={glossArcPath}
          stroke="#FFFFFF"
          strokeWidth={1.5}
          strokeOpacity={0.55}
          strokeLinecap="round"
          fill="none"
        />
      </svg>

      {/* Score labels + vote-share badges at each wedge centroid */}
      {segments.map((segment, index) => {
        if (!segment.label) return null;

        const { startAngle, endAngle } = segmentAngles[index];
        const midAngle = (startAngle + endAngle) / 2;
        const centroid = polarToCartesian(cx, cy, labelRadius, midAngle);
        const textColor = isLightColor(segment.color) ? '#000' : '#fff';

        return (
          <SegmentLabel key={`label-${segment.id}`} style={{ left: centroid.x, top: centroid.y }}>
            <span
              style={{
                fontWeight: 700,
                fontSize: labelFontSize,
                lineHeight: 1,
                color: textColor,
                whiteSpace: 'nowrap'
              }}
            >
              {segment.label}
            </span>

            {segment.actualPercentage > 0 && (
              <PercentageBadge style={{ backgroundColor: textColor, color: segment.color, fontSize: badgeFontSize }}>
                {Math.round(segment.actualPercentage)}%
              </PercentageBadge>
            )}
          </SegmentLabel>
        );
      })}

      <HubOverlay>
        <Hub style={{ width: hubSize, height: hubSize }}>
          {centerContent}

          {/* Inner shadow + top-left highlight, same as the mobile hub */}
          <svg
            width={hubSize}
            height={hubSize}
            style={{ position: 'absolute', inset: 0 }}
            viewBox={`0 0 ${hubSize} ${hubSize}`}
          >
            <defs>
              <radialGradient id="dialInnerShadow" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="55%" stopColor="#7892be" stopOpacity="0" />
                <stop offset="82%" stopColor="#1F3A66" stopOpacity="0.08" />
                <stop offset="95%" stopColor="#1F3A66" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#0E2547" stopOpacity="0.2" />
              </radialGradient>
              <radialGradient id="dialHighlight" cx="32%" cy="28%" r="55%" fx="32%" fy="28%">
                <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.55" />
                <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0.05" />
                <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx={hubSize / 2} cy={hubSize / 2} r={hubSize / 2} fill="url(#dialHighlight)" />
            <circle cx={hubSize / 2} cy={hubSize / 2} r={hubSize / 2} fill="url(#dialInnerShadow)" />
          </svg>
        </Hub>
      </HubOverlay>
    </WheelContainer>
  );
};

export default PredictionWheel;
